import * as Sentry from '@sentry/tanstackstart-react'
import { toast } from 'sonner'
import type { OutgoingMessage, WebsocketMessage } from '@/types/websocket'
import type { AuthResponse } from '@/tygo/dto'
import { useBalanceStore } from '@/domains/balance/store'
import { useGameStore } from '@/domains/game/store'
import { getQueryClient } from '@/integrations/tanstack-query/root-provider'
import { authQueryOptions } from '@/domains/auth/queries'
import { apiFetch } from '@/lib/http'
import { playSound } from '@/lib/sounds'

const MAX_RECONNECT_ATTEMPTS = 5
const BASE_RECONNECT_DELAY_MS = 1000

class WebsocketClient {
  private ws: WebSocket | null = null
  private reconnectAttempts = 0
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  private intentionalClose = false

  connect() {
    if (this.connectionExists()) return

    this.intentionalClose = false
    this.reconnectAttempts = 0
    this.establishConnection()
  }

  disconnect() {
    if (!this.connectionExists()) return

    this.intentionalClose = true
    this.clearReconnectTimeout()
    this.ws?.close()
    this.ws = null
  }

  send(message: OutgoingMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message')
      return
    }

    this.ws.send(JSON.stringify(message))
  }

  private establishConnection() {
    const authData = getQueryClient().getQueryData(
      authQueryOptions('').queryKey,
    )

    // If the user is not authenticated, don't establish a connection. This is defensive
    if (!authData?.access_token) return

    this.ws = new WebSocket(
      `${import.meta.env.VITE_API_URL}/ws?token=${authData.access_token}`,
    )
    this.ws.onopen = () => {
      console.log('Connected to websocket')
      this.reconnectAttempts = 0
    }
    this.ws.onmessage = (event) => {
      try {
        const msg: WebsocketMessage = JSON.parse(event.data)
        this.handleMessage(msg)
      } catch (err) {
        Sentry.captureException(err, {
          tags: { component: 'websocket' },
          extra: {
            rawData:
              typeof event.data === 'string'
                ? event.data.slice(0, 500)
                : 'non-string',
          },
        })
      }
    }
    this.ws.onclose = (event) => {
      console.log('Disconnected from websocket', {
        code: event.code,
        reason: event.reason,
      })
      this.ws = null

      // Don't reconnect if the close was intentional (e.g., user logout)
      // or if code 1000 (normal closure)
      if (!this.intentionalClose && event.code !== 1000) {
        this.attemptReconnect()
      }
    }
    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      Sentry.captureException(new Error('WebSocket connection error'), {
        tags: { component: 'websocket' },
      })
    }
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached')
      toast.error('Connection lost. Please refresh the page.')
      Sentry.captureMessage('WebSocket max reconnection attempts reached', {
        level: 'warning',
        tags: { component: 'websocket' },
      })
      return
    }

    const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts)
    this.reconnectAttempts++

    console.log(
      `Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
    )

    this.reconnectTimeout = setTimeout(async () => {
      // Refresh the access token before reconnecting so we don't use a stale one
      try {
        const authData = await apiFetch<AuthResponse>('/api/v1/auth/refresh', {
          method: 'GET',
        })
        getQueryClient().setQueryData(authQueryOptions('').queryKey, authData)
      } catch {
        // If refresh fails, still attempt connection with cached token
      }
      this.establishConnection()
    }, delay)
  }

  private clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }
  }

  private handleMessage(message: WebsocketMessage) {
    switch (message.type) {
      case 'balance_update': {
        useBalanceStore.setState({ balance: message.payload.balance })
        break
      }
      case 'player_join': {
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'game_start': {
        playSound('game-start')
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'new_hand': {
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'player_leave': {
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'hand_complete': {
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'action_timeout': {
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'player_fold': {
        playSound('hand-fold')
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'player_check': {
        playSound('hand-check')
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'player_call':
      case 'player_bet':
      case 'player_raise': {
        playSound('hand-bet')
        useGameStore.setState({ game: message.payload.game_state })
        break
      }
      case 'removed_from_game': {
        useGameStore.getState().clearGame()
        toast.info(
          'You ran out of chips and have been removed from the game.',
        )
        setTimeout(() => {
          window.location.href = '/'
        }, 2000)
        break
      }
      case 'error': {
        toast.error(message.payload.message)
        break
      }
      default: {
        // Exhaustive check: ensures all message types are handled
        const _exhaustive: never = message
        void _exhaustive
        break
      }
    }
  }

  private connectionExists(): boolean {
    return !!(
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    )
  }
}

export const websocketClient = new WebsocketClient()
